import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box, Chip, Divider, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CardWrapper, ButtonOutlined } from '../../UI';
import { agruparHistoricosPorData } from '../Historico/historicoController';
import DialogNovoEmail from '../Email/DialogNovoEmail';
import * as C from '../../../theme/colors.constants';

/**
 * Aba E-mail (Processo) — print `grid_check_email.png`: chip cinza com a data
 * do dia, e abaixo a lista de e-mails com bolinha, metadados em negrito e o
 * corpo preservando as quebras de linha originais (`white-space: pre-line`).
 *
 * AW2-1092 Etapa 0: `emails` (array cru do DTO real, já buscado pelo
 * container via `buscarEmailsProcesso`) substitui o mock estático `EMAILS`.
 * Agrupamento por data reaproveita `agruparHistoricosPorData`
 * (`Historico/historicoController.js`, já em produção no protótipo para a
 * aba Histórico/Acompanhamento — mesmo `ProcessoHistoricoDTO`, mesmo
 * problema de agrupamento) — decisão deliberada de NÃO reescrever essa
 * lógica aqui. Só a função pura de agrupamento é reaproveitada, não o
 * componente de apresentação `ListaHistorico.jsx` (que sempre desenha a
 * bolinha de "fixar" colorida por tipo, mesmo desabilitada — divergiria do
 * visual simples já calibrado contra o print desta aba).
 *
 * **Achado ao testar com dado real (processo 1000, não previsto no plano):**
 * boa parte dos corpos de e-mail reais vêm do backend como HTML puro
 * (`<p>À</p><p> Dois irmãos...</p>`), diferente do texto plano assumido ao
 * ler só `DialogEmail.jsx` (textarea simples) — outros fluxos de disparo
 * (templates de notificação) geram HTML de verdade. `agruparHistoricosPorData`/
 * `formatarPartesTexto` (reaproveitados de `Historico/historicoController.js`)
 * já são seguros (nunca interpretam HTML — texto React comum, auto-escapado),
 * mas isso só evita XSS, não evita as tags aparecerem LITERALMENTE na tela
 * (`<p>` visível como texto). Corrigido convertendo `<\/p>`/`<br>` em quebra
 * de linha e removendo as demais tags ANTES de agrupar (`prepararConteudoEmail`,
 * função local) — mesma filosofia de segurança de `clienteController.
 * removerTagsHtml` (nunca `dangerouslySetInnerHTML`/parser HTML), mas
 * preservando quebra de parágrafo (aquela função colapsa espaços/quebras,
 * o que aqui destruiria a legibilidade de um corpo de e-mail real).
 *
 * "+ Novo" (Etapa 1): `DialogNovoEmail.jsx` novo — registra um e-mail via
 * `registrarEmailsProcesso`. Gate `isComercialPrincipal` (`dadosCliente?.
 * idUsuarioComercial === idUsuarioLogado`, via `useSelector` local, mesmo
 * padrão de `TableAcordos.jsx`) reaproveita `dadosCliente` já buscado pelo
 * container (AW2-1136 Etapa 0) — zero fetch novo. Botão inteiro some quando
 * `ehFaseAcordoSeguroExterno` (mesma flag já computada no container e usada
 * por `TableAcordos`), mesma regra da tela real. Estado do dialog resetado
 * ao trocar de `idProcesso` — este componente sobrevive à troca de processo
 * enquanto a aba "email" continuar ativa (mesmo padrão de `AbaDocumentos.jsx`).
 */
function prepararConteudoEmail(historico) {
  const textoConvertido = (historico.texto || '')
    .replace(/<\/p>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { ...historico, texto: textoConvertido };
}

export default function AbaEmail({
  emails, dadosCliente, ehFaseAcordoSeguroExterno, idProcesso, onEmailRegistrado,
}) {
  const grupos = useMemo(() => {
    const conteudoPreparado = (emails || []).map(prepararConteudoEmail);
    return agruparHistoricosPorData(conteudoPreparado);
  }, [emails]);

  const idUsuarioLogado = useSelector(states => states.authentication.userInformation.idUsuario);
  const isComercialPrincipal = dadosCliente?.idUsuarioComercial === idUsuarioLogado;

  const [dialogNovoEmailAberto, setDialogNovoEmailAberto] = useState(false);

  useEffect(() => {
    setDialogNovoEmailAberto(false);
  }, [idProcesso]);

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'flex-end', mb: 1.5, px: 0.5,
      }}
      >
        {!ehFaseAcordoSeguroExterno && (
          <ButtonOutlined
            startIcon={<AddIcon />}
            disabled={!isComercialPrincipal}
            onClick={() => setDialogNovoEmailAberto(true)}
          >
            NOVO
          </ButtonOutlined>
        )}
      </Box>

      <CardWrapper>
        {emails !== null && grupos.length === 0 && (
          <Typography variant="body2" sx={{ color: C.warmGrey, textAlign: 'center', py: 4 }}>
            Nenhum e-mail encontrado.
          </Typography>
        )}

        {grupos.map(grupo => (
          <Box key={`${grupo.dataLabel}-${grupo.itens[0].idProcessoHistorico}`}>
            <Chip
              label={grupo.dataLabel}
              sx={{
                backgroundColor: C.warmGrey,
                color: C.white,
                fontWeight: 700,
                borderRadius: 1.5,
                mb: 2,
              }}
            />

            {grupo.itens.map((historico, i) => (
              <Box key={historico.idProcessoHistorico}>
                <Box sx={{ display: 'flex', gap: 1.5, py: 1.5 }}>
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      backgroundColor: C.navy,
                      mt: 0.75,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 700 }}>Data: </Box>
                      {historico.dataHora}
                    </Typography>
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 700 }}>Autor: </Box>
                      {historico.usuario}
                    </Typography>
                    <Typography variant="body2">
                      <Box component="span" sx={{ fontWeight: 700 }}>Fase Atual: </Box>
                      {historico.faseAtualProcesso}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>E-mail: </Box>
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-line', color: C.black, mt: 0.25 }}
                    >
                      {historico.partesTexto.map(parte => parte.texto).join('')}
                    </Typography>
                  </Box>
                </Box>
                {i < grupo.itens.length - 1 ? <Divider /> : null}
              </Box>
            ))}
          </Box>
        ))}
      </CardWrapper>

      <DialogNovoEmail
        open={dialogNovoEmailAberto}
        idProcesso={idProcesso}
        onClose={() => setDialogNovoEmailAberto(false)}
        onSucesso={onEmailRegistrado}
      />
    </Box>
  );
}

AbaEmail.propTypes = {
  emails: PropTypes.arrayOf(PropTypes.object),
  dadosCliente: PropTypes.shape({
    idUsuarioComercial: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  ehFaseAcordoSeguroExterno: PropTypes.bool,
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onEmailRegistrado: PropTypes.func,
};

AbaEmail.defaultProps = {
  emails: null,
  dadosCliente: null,
  ehFaseAcordoSeguroExterno: false,
  idProcesso: null,
  onEmailRegistrado: () => null,
};
